import React, { useState } from 'react';
import VoucherEntry from './components/VoucherEntry';
import OTPVerify from './components/OTPVerify';
import ExperienceSelect from './components/ExperienceSelect';
import DateTimeSelect from './components/DateTimeSelect';

// Import styles
import '../../../css/booking-app.css';
import 'flatpickr/dist/flatpickr.min.css';

const STEPS = ['entry', 'otp', 'experience', 'datetime', 'success'];
const STEP_LABELS = {
    entry: 'Enter your voucher code to begin.',
    otp: 'Securely verify your identity.',
    experience: 'Select your preferred experience.',
    datetime: 'Choose a date and time to book.',
    success: 'You are booked!',
};

const App = () => {
    const [step, setStep] = useState('entry');
    const [voucherData, setVoucherData] = useState(null);
    const [email, setEmail] = useState('');
    const [sessionToken, setSessionToken] = useState(null);
    const [selectedExperience, setSelectedExperience] = useState(null);
    const [bookingReference, setBookingReference] = useState('');

    const stepIndex = STEPS.indexOf(step);

    return (
        <div className="rll-booking-app">
            {/* Step Dots */}
            <div className="rll-step-indicator">
                {STEPS.slice(0, -1).map((s, i) => {
                    const isDev = process.env.NODE_ENV === 'development';
                    return (
                        <div
                            key={s}
                            onClick={() => {
                                if (isDev) setStep(s);
                            }}
                            style={{ cursor: isDev ? 'pointer' : 'default' }}
                            className={`rll-step-dot ${i === stepIndex ? 'active' : ''} ${i < stepIndex ? 'completed' : ''}`}
                            title={isDev ? `Jump to ${s}` : ''}
                        />
                    );
                })}
            </div>

            {/* Header */}
            <div className="rll-booking-header">
                <h2>Redeem Your Experience</h2>
                <p>{STEP_LABELS[step]}</p>
            </div>

            {/* Step Content */}
            <div className="rll-animate-in" key={step}>
                {step === 'entry' && (
                    <VoucherEntry
                        onSuccess={(data, userEmail) => {
                            setVoucherData(data);
                            setEmail(userEmail);
                            setStep('otp');
                        }}
                    />
                )}

                {step === 'otp' && (
                    <OTPVerify
                        voucherCode={voucherData?.code}
                        email={email}
                        onBack={() => setStep('entry')}
                        onSuccess={(token, fullVoucherData) => {
                            setSessionToken(token);
                            setVoucherData(fullVoucherData);
                            if (fullVoucherData.allow_multi) {
                                setStep('experience');
                            } else {
                                setSelectedExperience(fullVoucherData.product_id);
                                setStep('datetime');
                            }
                        }}
                    />
                )}

                {step === 'experience' && (
                    <ExperienceSelect
                        voucherData={voucherData}
                        sessionToken={sessionToken}
                        onBack={() => setStep('otp')}
                        onSelected={(expId) => {
                            setSelectedExperience(expId);
                            setStep('datetime');
                        }}
                    />
                )}

                {step === 'datetime' && (
                    <DateTimeSelect
                        voucherData={voucherData}
                        experienceId={selectedExperience}
                        sessionToken={sessionToken}
                        customerEmail={email}
                        onBack={() => {
                            if (voucherData?.allow_multi) {
                                setStep('experience');
                            } else {
                                setStep('entry');
                            }
                        }}
                        onSuccess={(ref) => {
                            setBookingReference(ref || '');
                            setStep('success');
                        }}
                    />
                )}

                {step === 'success' && (
                    <div className="rll-success-screen">
                        <div className="rll-success-icon">
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h3>Booking Confirmed!</h3>
                        <p>Your experience has been successfully booked.<br />You will receive a confirmation email shortly.</p>
                        {bookingReference && (
                            <p style={{ marginTop: '1rem', fontWeight: 700, fontSize: '1.1rem', color: 'var(--rll-gray-900)' }}>
                                Reference: <span style={{ color: 'var(--rll-red)' }}>{bookingReference}</span>
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default App;
